<template>
	<div
		:class="[
			'toggle',
			...variantClassList,
			disabled && 'is-disabled',
			checked && 'is-selected'
		]"
		:disabled="disabled"
	>
		<Label
			class="toggle__label"
			:labelText="labelText"
			:forElementId="uid"
			v-if="labelText"
		>
			<template v-slot:leading>
				<slot name="beforeLabel"> </slot>
			</template>

			<template v-slot:trailing>
				<slot name="afterLabel"> </slot>
			</template>
		</Label>
		<div class="toggle__body">
			<div class="toggle__input-container">
				<span class="toggle__tooltip" v-if="actionSaved || errorText">
					<Tooltip :visible="actionSaved && errorText.length === 0">
						{{ 'Change Saved' }}
					</Tooltip>
					<Tooltip
						:visible="errorText.length > 0"
						state="negative"
						ref="errorTooltip"
					>
						{{ errorText }}
					</Tooltip>
				</span>
				<input
					class="toggle__input"
					type="checkbox"
					name="toggle"
					:id="uid"
					v-model="isChecked"
					@change="onChange"
					:aria-label="ariaLabel"
				/>
				<span
					ref="insideLabelOffSpan"
					:class="[
						'toggle__inside-label-off',
						`is-option-${insideLabelOption}`
					]"
					v-if="
						(insideLabelOption === 1 || insideLabelOption === 2) &&
							!checked
					"
				>
					{{ insideLabelOptions[insideLabelOption - 1][0] }}
				</span>
				<span
					ref="insideLabelOnSpan"
					:class="[
						'toggle__inside-label-on',
						`is-option-${insideLabelOption}`
					]"
					v-if="
						(insideLabelOption === 1 || insideLabelOption === 2) &&
							checked
					"
				>
					{{ insideLabelOptions[insideLabelOption - 1][1] }}
				</span>
			</div>
		</div>
		<HelpFeedback
			div
			class="toggle__help-feedback"
			:helpText="helpText"
			v-if="helpText"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import baseCheckboxMixin from '@/mixins/base-checkbox-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/toggle-variant-classes';

export default {
	name: 'Toggle',
	components: {
		Label,
		HelpFeedback,
		Tooltip
	},
	mixins: [baseComponentMixin, componentVariantMixin, baseCheckboxMixin],
	props: {
		helpText: {
			type: String,
			default: null
		},
		insideLabelOption: {
			type: Number,
			default: 1
		},
		actionSaved: {
			type: Boolean,
			default: false
		},
		errorText: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			insideLabelOptions: [
				['OFF', 'ON'],
				['NO', 'YES']
			]
		};
	},
	created() {
		this.variantClasses = variantClasses;
	}
};
</script>

<style lang="scss" scoped>
.toggle {
	display: inline-flex;
	font-size: $field-font-size;
	color: var(--field-fg);

	&__body {
		display: flex;
		align-items: center;
		height: 18px;
	}

	&__input-container {
		position: relative;

		&:focus-within {
			&::after {
				content: '';
				display: inline-block;
				position: absolute;
				width: calc(54px + 8px);
				height: calc(100% + 8px);
				@include transform-full-center;
				border: $field-border-width $focus-style var(--brand-2);
				border-radius: 24px;
			}
		}
	}

	&__tooltip {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		bottom: calc(100% + #{$gutter-md});
		white-space: nowrap;
	}

	&__input {
		position: relative;
		-webkit-appearance: none;
		appearance: none;
		outline: none;
		width: 54px;
		height: 24px;
		border-radius: 24px;
		background-color: var(--field-bg);
		border: $field-border;
		cursor: pointer;
		@include flex-vertical-center;
		@include animation(all);
		z-index: $z-level1-1;

		&::before {
			display: inline-block;
			position: absolute;
			left: -1px;
			content: '';
			@include create-circle(24px, 24px, var(--bg-0), $field-border);
			box-shadow: 1px 2px 4px hsla(0, 0%, 0%, 0.2);
			@include animation(left);
			z-index: $z-level1-2;
		}

		&:checked {
			background-color: var(--ui-positive);
			border-color: var(--ui-positive);

			&::before {
				left: calc(100% - 23px);
				@include animation(left);
			}
		}

		&::after {
			display: none;
			position: absolute;
			content: '';
			width: calc(100% + 2px);
			height: calc(100% + 2px);
			@include transform-full-center;
			border: $focus-width $focus-style var(--brand-2);
			border-radius: 24px;
		}

		&:hover::after {
			display: inline-block;
		}
	}

	&__label {
		margin-right: $gutter-md;
		cursor: pointer;
	}

	&__inside-label-off,
	&__inside-label-on {
		font-size: 11px;
		font-weight: $bold-font-weight;
		position: absolute;
		pointer-events: none;
		z-index: $z-level1-2;
		@include transform-vertical-center;
	}

	&__inside-label-off {
		&.is-option-1 {
			right: 7px;
		}

		&.is-option-2 {
			right: 8px;
		}
	}

	&__inside-label-on {
		color: $neutral-0;

		&.is-option-1 {
			left: 8px;
		}

		&.is-option-2 {
			left: 7px;
		}
	}

	&--condensed {
		.toggle {
			&__input {
				height: 12px;

				&::before {
					width: 34px;
					height: 20px;
					left: -3px;
					border-radius: 11px;
				}

				&:checked {
					background-color: var(--bg-0);
					border-color: var(--neutral-3);

					&::before {
						left: calc(100% - 32px);
						background: var(--ui-positive);
						border-color: transparent;
					}
				}

				&:focus {
					&::after {
						display: inline-block;
					}

					&::before {
						border-color: var(--brand-2);
						border-width: $focus-width;
					}
				}
			}

			&__input-container:focus-within {
				&::after {
					display: none;
				}
			}

			&__inside-label-off {
				right: initial;

				&.is-option-1 {
					left: 4px;
				}

				&.is-option-2 {
					left: 6px;
				}
			}

			&__inside-label-on {
				left: initial;

				&.is-option-1 {
					right: 8px;
				}

				&.is-option-2 {
					right: 7px;
				}
			}
		}
	}
}
</style>
